import { useId } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import {
  Button,
  Checkbox,
  Input,
  InputBase,
} from "@telluscompany/design-system";
import { Upload } from "lucide-react";
import { Controller } from "react-hook-form";

import { useLocalization } from "@i18n";
import { readOnlyInputClass } from "@shared/components";

import type { AdminStructureDetail } from "../../api/structures/structures.types";
import {
  centralPointSectionTitleKey,
  PIN_SECTION_INFO_ARIA_LABEL_KEY,
  PIN_SECTION_INFO_TOOLTIP_KEY,
  PIN_SECTION_TITLE_KEY,
  structureNameLabelKey,
} from "../../schemas";
import { STRUCTURE_GENERAL_DATA_FORM_ID } from "./adminStructureGeneralDataForm.context.definition";
import { AdminStructureGeneralDataFormProvider } from "./AdminStructureGeneralDataFormProvider";
import { useAdminStructureGeneralDataFormContext } from "./useAdminStructureGeneralDataFormContext";
import type { StructureModalTabId } from "./admin-structure-modal-tabs.config";
import { formIdForStructureModalTab } from "./structure-modal-form-ids";
import type {
  CoordinateSystem,
  StructureOperationalStatus,
  StructureType,
} from "../../api/structures/structures.types";

type NativeSelectOption = { value: string; label: string };

const NativeSelect = ({
  label,
  value,
  onChange,
  options,
  disabled,
  clearable,
  placeholderOption,
  errorMessage,
  form,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: NativeSelectOption[];
  disabled?: boolean;
  clearable?: boolean;
  placeholderOption?: string;
  errorMessage?: string;
  form?: string;
}) => (
  <label className="group flex min-w-0 w-full flex-col gap-1">
    <span className="text-label group-aria-invalid:text-error">{label}</span>
    <select
      aria-label={label}
      aria-invalid={Boolean(errorMessage) || undefined}
      disabled={disabled}
      value={value}
      form={form}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm shadow-sm disabled:opacity-60 aria-invalid:border-error aria-invalid:ring-[3px] aria-invalid:ring-error/20"
    >
      {clearable ? (
        <option value="">{placeholderOption ?? " "}</option>
      ) : null}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {errorMessage ? (
      <span className="text-error text-sm">{errorMessage}</span>
    ) : null}
  </label>
);

const GeneralDataNumberField = ({
  label,
  value,
  onChange,
  disabled,
  errorMessage,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  errorMessage?: string;
}) => (
  <label className="group flex min-w-0 w-full flex-col gap-1">
    <span className="text-label group-aria-invalid:text-error">{label}</span>
    <input
      type="number"
      aria-label={label}
      aria-invalid={Boolean(errorMessage) || undefined}
      disabled={disabled}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm shadow-sm disabled:opacity-60 aria-invalid:border-error aria-invalid:ring-[3px] aria-invalid:ring-error/20"
    />
    {errorMessage ? (
      <span className="text-error text-sm">{errorMessage}</span>
    ) : null}
  </label>
);

const GeneralDataDateField = ({
  label,
  value,
  onChange,
  disabled,
  errorMessage,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  errorMessage?: string;
}) => (
  <label className="group flex min-w-0 w-full flex-col gap-1">
    <span className="text-label group-aria-invalid:text-error">{label}</span>
    <input
      type="date"
      aria-label={label}
      aria-invalid={Boolean(errorMessage) || undefined}
      disabled={disabled}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm shadow-sm disabled:opacity-60 aria-invalid:border-error aria-invalid:ring-[3px] aria-invalid:ring-error/20"
    />
    {errorMessage ? (
      <span className="text-error text-sm">{errorMessage}</span>
    ) : null}
  </label>
);

const useGeneralDataFormContext = useAdminStructureGeneralDataFormContext;

const STRUCTURE_TYPES: StructureType[] = ["dam", "stockpile", "openPit"];
const OPERATIONAL_STATUSES = [
  "decommissioned",
  "underConstruction",
  "inOperation",
  "underConstructionAndOperation",
  "underDecommissioning",
  "halted",
] as const;
const COORDINATE_SYSTEMS = [
  "sirgas2000",
  "astrochua",
  "corregoAlegre",
  "sad69",
  "wgs84",
] as const;
const UTM_HEMISPHERES = ["north", "south"] as const;

const orthophotoDisplayName = (
  storagePath: string | null | undefined,
): string | null => {
  if (!storagePath) {
    return null;
  }
  const segments = storagePath.split("/");
  return segments[segments.length - 1] || storagePath;
};

export const AdminStructureGeneralDataTypeField = () => {
  const { translate } = useLocalization();
  const { canWrite, typeLocked, form, embedForm } = useGeneralDataFormContext();

  const structureTypeOptions = STRUCTURE_TYPES.map((value) => ({
    value,
    label: translate(`admin.structures.structureType.${value}`),
  }));

  return (
    <Controller
      control={form.control}
      name="type"
      render={({ field, fieldState }) => (
        <NativeSelect
          label={translate("admin.structures.generalData.label.type")}
          value={field.value ?? ""}
          onChange={(value) =>
            field.onChange(value === "" ? undefined : (value as StructureType))
          }
          options={structureTypeOptions}
          disabled={!canWrite || typeLocked}
          clearable={!typeLocked}
          placeholderOption={translate(
            "admin.structures.generalData.placeholder.type",
          )}
          errorMessage={fieldState.error?.message}
          form={embedForm ? undefined : STRUCTURE_GENERAL_DATA_FORM_ID}
        />
      )}
    />
  );
};

export const AdminStructureGeneralDataFieldsGate = () => {
  const { detail, form, embedForm, onSubmit, onInvalidSubmit } =
    useGeneralDataFormContext();
  const watchedType = form.watch("type");
  const showGeneralFields = Boolean(detail.type) || Boolean(watchedType);

  if (!showGeneralFields) {
    return null;
  }

  const fields = <AdminStructureGeneralDataFields />;

  if (!embedForm) {
    return (
      <form
        id={STRUCTURE_GENERAL_DATA_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)}
        noValidate
        className="flex flex-col gap-4"
      >
        {fields}
      </form>
    );
  }

  return fields;
};

export const AdminStructureGeneralDataFields = () => {
  const { translate } = useLocalization();
  const orthophotoInputId = useId();
  const {
    detail,
    canWrite,
    pendingOrthophotoFile,
    setPendingOrthophotoFile,
    uploadPendingOrthophoto,
    isUploadingOrthophoto,
    form,
  } = useGeneralDataFormContext();

  const watchedType = form.watch("type") ?? detail.type;
  const utmEnabled = form.watch("utmEnabled");

  const orthophotoFileName =
    pendingOrthophotoFile?.name ??
    orthophotoDisplayName(detail.orthophotoStoragePath);

  const handleOrthophotoSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    setPendingOrthophotoFile(file);
  };

  const nameLabel = translate(structureNameLabelKey(watchedType ?? detail.type));
  const centralSectionTitle = translate(
    centralPointSectionTitleKey(watchedType ?? detail.type),
  );
  const pinSectionTitle = translate(PIN_SECTION_TITLE_KEY);
  const pinInfoTooltip = translate(PIN_SECTION_INFO_TOOLTIP_KEY);
  const pinInfoAriaLabel = translate(PIN_SECTION_INFO_ARIA_LABEL_KEY);

  const operationalStatusOptions = OPERATIONAL_STATUSES.map((value) => ({
    value,
    label: translate(`admin.structures.operationalStatus.${value}`),
  }));
  const coordinateSystemOptions = COORDINATE_SYSTEMS.map((value) => ({
    value,
    label: translate(`admin.structures.generalData.coordinateSystem.${value}`),
  }));
  const utmHemisphereOptions = UTM_HEMISPHERES.map((value) => ({
    value,
    label: translate(`admin.structures.generalData.utmHemisphere.${value}`),
  }));

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm">{nameLabel}</span>
          <InputBase
            type="text"
            value={detail.name}
            disabled
            readOnly
            tabIndex={-1}
            className={readOnlyInputClass}
            aria-label={nameLabel}
          />
        </label>
        <Input
          type="text"
          control={form.control}
          label={translate(
            "admin.structures.generalData.label.responsibleCompany",
          )}
          name="responsibleCompany"
          disabled={!canWrite}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Controller
          control={form.control}
          name="operationalStatus"
          render={({ field, fieldState }) => (
            <NativeSelect
              label={translate(
                "admin.structures.generalData.label.operationalStatus",
              )}
              value={field.value ?? ""}
              onChange={(value) =>
                field.onChange(value as StructureOperationalStatus)
              }
              options={operationalStatusOptions}
              disabled={!canWrite}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={form.control}
          name="operationStartDate"
          render={({ field, fieldState }) => (
            <GeneralDataDateField
              label={translate(
                "admin.structures.generalData.label.operationStartDate",
              )}
              value={field.value ?? ""}
              onChange={field.onChange}
              disabled={!canWrite}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={form.control}
          name="operationEndDate"
          render={({ field }) => (
            <GeneralDataDateField
              label={translate(
                "admin.structures.generalData.label.operationEndDate",
              )}
              value={field.value ?? ""}
              onChange={(value) => field.onChange(value || undefined)}
              disabled={!canWrite}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-3 gap-x-4 gap-y-4">
        <h3 className="col-span-3 text-sm font-bold">{centralSectionTitle}</h3>
        <Controller
          control={form.control}
          name="centralPointCoordinateSystem"
          render={({ field, fieldState }) => (
            <NativeSelect
              label={translate(
                "admin.structures.generalData.label.centralPointSystem",
              )}
              value={field.value ?? ""}
              onChange={(value) => field.onChange(value as CoordinateSystem)}
              options={coordinateSystemOptions}
              disabled={!canWrite}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <div className="min-w-0 w-full">
          <Input
            type="text"
            control={form.control}
            label={translate("admin.structures.generalData.label.centralPointE")}
            name="centralPointE"
            disabled={!canWrite}
            className="w-full"
          />
        </div>
        <div className="min-w-0 w-full">
          <Input
            type="text"
            control={form.control}
            label={translate("admin.structures.generalData.label.centralPointN")}
            name="centralPointN"
            disabled={!canWrite}
            className="w-full"
          />
        </div>

        <div className="col-span-3 flex items-center gap-1">
          <h3 className="text-sm font-bold">{pinSectionTitle}</h3>
          <Tooltip title={pinInfoTooltip} arrow placement="top">
            <button
              type="button"
              aria-label={pinInfoAriaLabel}
              className="flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </button>
          </Tooltip>
        </div>
        <Controller
          control={form.control}
          name="pinCoordinateSystem"
          render={({ field, fieldState }) => (
            <NativeSelect
              label={translate("admin.structures.generalData.label.pinSystem")}
              value={field.value ?? ""}
              onChange={(value) =>
                field.onChange(value === "" ? null : (value as CoordinateSystem))
              }
              options={coordinateSystemOptions}
              disabled={!canWrite}
              clearable
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <div className="min-w-0 w-full">
          <Input
            type="text"
            control={form.control}
            label={translate("admin.structures.generalData.label.pinE")}
            name="pinE"
            disabled={!canWrite}
            className="w-full"
          />
        </div>
        <div className="min-w-0 w-full">
          <Input
            type="text"
            control={form.control}
            label={translate("admin.structures.generalData.label.pinN")}
            name="pinN"
            disabled={!canWrite}
            className="w-full"
          />
        </div>

        <div className="min-w-0 w-full self-center">
          <Checkbox
            control={form.control}
            label={translate("admin.structures.generalData.label.utmEnabled")}
            name="utmEnabled"
            disabled={!canWrite}
          />
        </div>
        {utmEnabled ? (
          <>
            <Controller
              control={form.control}
              name="utmZone"
              render={({ field, fieldState }) => (
                <GeneralDataNumberField
                  label={translate(
                    "admin.structures.generalData.label.utmZone",
                  )}
                  value={field.value?.toString() ?? ""}
                  onChange={(value) =>
                    field.onChange(value === "" ? undefined : Number(value))
                  }
                  disabled={!canWrite}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={form.control}
              name="utmHemisphere"
              render={({ field, fieldState }) => (
                <NativeSelect
                  label={translate(
                    "admin.structures.generalData.label.utmHemisphere",
                  )}
                  value={field.value ?? ""}
                  onChange={(value) =>
                    field.onChange(
                      value === "" ? null : (value as "north" | "south"),
                    )
                  }
                  options={utmHemisphereOptions}
                  disabled={!canWrite}
                  clearable
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </>
        ) : null}
      </div>

      {canWrite ? (
        <div className="flex flex-col gap-1">
          <span className="text-sm">
            {translate("admin.structures.generalData.label.orthophoto")}
          </span>
          <div className="flex gap-2">
            <div className="relative min-w-0 flex-1">
              <input
                id={orthophotoInputId}
                type="file"
                accept=".tif,image/tiff"
                className="sr-only"
                aria-label={translate(
                  "admin.structures.generalData.label.orthophoto",
                )}
                onChange={handleOrthophotoSelect}
              />
              <button
                type="button"
                className="flex h-10 w-full items-center justify-between rounded-xl border border-border px-4 text-sm shadow-sm"
                onClick={() =>
                  document.getElementById(orthophotoInputId)?.click()
                }
              >
                <span
                  className={`truncate ${orthophotoFileName ? "text-foreground" : "text-muted-foreground"}`}
                >
                  {orthophotoFileName ??
                    translate(
                      "admin.structures.generalData.placeholder.orthophoto",
                    )}
                </span>
                <Upload size={18} aria-hidden />
              </button>
            </div>
            {pendingOrthophotoFile ? (
              <Button
                type="button"
                variant="secondary"
                disabled={isUploadingOrthophoto}
                onClick={() => void uploadPendingOrthophoto()}
              >
                {translate(
                  "admin.structures.generalData.button.uploadOrthophoto",
                )}
              </Button>
            ) : null}
          </div>
        </div>
      ) : orthophotoFileName ? (
        <div className="flex flex-col gap-1">
          <span className="text-sm">
            {translate("admin.structures.generalData.label.orthophoto")}
          </span>
          <span className="truncate text-sm text-foreground">
            {orthophotoFileName}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export const AdminStructureGeneralDataFooter = ({
  onCancel,
  variant = "standalone",
  activeTab = "generalData",
  isTabSaving = false,
}: {
  onCancel?: () => void;
  variant?: "modal" | "standalone";
  /** Aba ativa no modal — define qual `<form>` o Salvar do footer submete. */
  activeTab?: StructureModalTabId;
  isTabSaving?: boolean;
}) => {
  const { translate } = useLocalization();
  const { canWrite, isSaving: isGeneralDataSaving } = useGeneralDataFormContext();

  if (!canWrite) {
    return null;
  }

  const submitFormId =
    variant === "modal"
      ? formIdForStructureModalTab(activeTab)
      : STRUCTURE_GENERAL_DATA_FORM_ID;

  const saveButton = submitFormId ? (
    <Button
      type="submit"
      form={submitFormId}
      variant="primary"
      disabled={isGeneralDataSaving || isTabSaving}
    >
      {translate("admin.structures.modal.button.save")}
    </Button>
  ) : null;

  const cancelButton =
    onCancel != null ? (
      <Button type="button" variant="secondary" onClick={onCancel}>
        {translate("admin.structures.modal.button.cancel")}
      </Button>
    ) : null;

  if (variant === "modal") {
    return (
      <div className="flex justify-end gap-4 border-t border-border pt-4">
        {cancelButton}
        {saveButton}
      </div>
    );
  }

  return (
    <div className="flex justify-end gap-4">
      {cancelButton}
      {saveButton}
    </div>
  );
};

export type AdminStructureGeneralDataTabProps = {
  complexId: string;
  structureId: string;
  canWrite: boolean;
  detail: AdminStructureDetail;
  onSaved: (detail: AdminStructureDetail) => void;
};

const AdminStructureGeneralDataTab = ({
  complexId,
  structureId,
  canWrite,
  detail,
  onSaved,
}: AdminStructureGeneralDataTabProps) => (
  <AdminStructureGeneralDataFormProvider
    complexId={complexId}
    structureId={structureId}
    canWrite={canWrite}
    detail={detail}
    onSaved={onSaved}
  >
    <div className="flex w-full flex-col gap-4 px-6 py-4">
      <div className="w-full max-w-[550px]">
        <AdminStructureGeneralDataTypeField />
      </div>
      <AdminStructureGeneralDataFieldsGate />
      <AdminStructureGeneralDataFooter />
    </div>
  </AdminStructureGeneralDataFormProvider>
);

export default AdminStructureGeneralDataTab;
